import React, {useMemo} from 'react'
import {isIOS} from 'react-device-detect'
import QRCode from 'react-qr-code'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import {
  AUTHENTICATOR_GOOGLE_APP_STORE_URL,
  AUTHENTICATOR_GOOGLE_PLAY_STORE_URL,
  AUTHENTICATOR_MICROSOFT_APP_STORE_URL,
  AUTHENTICATOR_MICROSOFT_PLAY_STORE_URL,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  copyToClipboard,
  getFontFamily,
  getTextShadow,
  showSnackbar,
  textSpacer,
  useLocation,
} from 'utils'
import {useTheme} from 'themes'
import {ObjectState, parseShape, SettingAuthenticatorScreenType} from 'types'
import {Button, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: ${convertUnit(16)};
  text-align: center;
  max-width: ${convertUnit(560)};
  margin-top: ${convertUnit(20)};
  padding: 0 ${convertUnit(20)};
  height: fit-content;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledBodyContainer = styled.div`
  overflow-y: scroll;
  scrollbar-width: none;
  padding-bottom: ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
`
const StyledTitle = styled(Paragraph)`
  margin: ${convertUnit(16)} 0 ${convertUnit(12)} 0;
`
const StyledTutorial = styled.div`
  display: flex;
  flex-direction: row;
  text-align: left;
  gap: ${convertUnit(8)};
`
const StyledQrContainer = styled.div`
  background-color: ${({theme}) => theme.white_3};
  display: flex;
  flex-direction: column;
  padding: ${convertUnit(20)} ${convertUnit(16)};
  border-radius: ${convertUnit(8)};
`
const StyledQrImageContainer = styled.div`
  padding: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  display: flex;
  justify-content: center;
  width: fit-content;
  align-self: center;
  margin-top: ${convertUnit(20)};
  box-shadow: ${({theme}) =>
    getTextShadow(theme, {blurRadius: 8, opacity: 0.14})};
`
const StyledSeparatorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${convertUnit(12)} 0;
  gap: ${convertUnit(8)};
`
const StyledSeparator = styled.div`
  height: ${convertUnit(1)};
  display: flex;
  flex: 1;
  background-color: ${({theme}) => theme.gray_5};
`
const StyledKeyContainer = styled.div`
  background-color: ${({theme}) => theme.white_3};
  display: flex;
  flex-direction: column;
  padding: ${convertUnit(8)};
  border-radius: ${convertUnit(8)};
`
const StyledCopyKeyText = styled(Paragraph)`
  cursor: pointer;
`
const StyledFooter = styled.div`
  margin-bottom: ${convertUnit(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(4)};
`

export interface GiftShopSettingAuthenticatorSetupProps {
  stateScreen: ObjectState<SettingAuthenticatorScreenType>
}
export default function GiftShopSettingAuthenticatorSetup({
  stateScreen,
}: GiftShopSettingAuthenticatorSetupProps) {
  const {translate} = useTranslation()
  const theme = useTheme()
  const {uri} = useLocation('giftshop_setting_authenticator').state
  const setScreen = stateScreen[1]

  const parseText = useMemo<parseShape[]>(
    () => [
      {
        pattern: /<b>.*?<\/b>/,
        style: {fontFamily: getFontFamily('medium')},
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
      {
        pattern: /<google>.*?<\/google>/,
        style: {
          fontFamily: getFontFamily('medium'),
          color: theme.primary_5,
          textDecorationLine: 'underline',
          cursor: 'pointer',
        },
        onClick: () =>
          window.open(
            isIOS
              ? AUTHENTICATOR_GOOGLE_APP_STORE_URL
              : AUTHENTICATOR_GOOGLE_PLAY_STORE_URL,
            '_blank',
          ),
        renderText: (text) => text.replace(/<google>|<\/google>/g, ''),
      },
      {
        pattern: /<ms>.*?<\/ms>/,
        style: {
          fontFamily: getFontFamily('medium'),
          color: theme.primary_5,
          textDecorationLine: 'underline',
          cursor: 'pointer',
        },
        onClick: () =>
          window.open(
            isIOS
              ? AUTHENTICATOR_MICROSOFT_APP_STORE_URL
              : AUTHENTICATOR_MICROSOFT_PLAY_STORE_URL,
            '_blank',
          ),
        renderText: (text) => text.replace(/<ms>|<\/ms>/g, ''),
      },
    ],
    [theme.primary_5],
  )

  const handleRenderSetupScreen = useMemo(
    () => (
      <StyledContainer>
        <StyledContentContainer>
          <StyledBodyContainer>
            <StyledTitle fontSize="xl" fontWeight="bold">
              {translate('giftShop:settingAuthAppTitle')}
            </StyledTitle>
            {uri && (
              <>
                <StyledQrContainer>
                  {Array.from({length: 2}).map((_, index) => (
                    <StyledTutorial key={`${index + 1}`}>
                      <Paragraph fontSize="m">{index + 1}.</Paragraph>
                      <Paragraph fontSize="m">
                        <ParsedText parse={parseText}>
                          {translate('giftShop:settingAuthAppTutorial', {
                            context: `${index + 1}`,
                          })}
                        </ParsedText>
                      </Paragraph>
                    </StyledTutorial>
                  ))}
                  <StyledQrImageContainer>
                    {uri ? (
                      <QRCode value={uri} size={150} />
                    ) : (
                      <Skeleton
                        width={150}
                        height={150}
                        baseColor={theme.gray_2}
                        highlightColor={theme.white_1}
                      />
                    )}
                  </StyledQrImageContainer>
                </StyledQrContainer>
                <StyledSeparatorContainer>
                  <StyledSeparator />
                  <Paragraph fontSize="m" color="gray_5">
                    {translate('giftShop:settingAuthAppEnterKey')}
                  </Paragraph>
                  <StyledSeparator />
                </StyledSeparatorContainer>
                <StyledKeyContainer>
                  <Paragraph fontSize="m" fontWeight="medium">
                    {textSpacer(uri.split('&secret=')[1])}
                  </Paragraph>
                  <StyledCopyKeyText
                    fontSize="m"
                    fontWeight="medium"
                    color="primary_5"
                    onClick={() =>
                      copyToClipboard(uri.split('&secret=')[1], () => {
                        showSnackbar(translate('giftShop:settingAuthAppCopied'))
                      })
                    }>
                    {translate('giftShop:settingAuthAppCopyKey')}
                  </StyledCopyKeyText>
                </StyledKeyContainer>
              </>
            )}
          </StyledBodyContainer>
          <StyledFooter>
            <Paragraph fontSize="m" color="gray_5">
              {translate('global:step')
                .concat(' ')
                .concat(translate('giftShop:xOfy', {x: 1, y: 3}))}
            </Paragraph>
            <Button
              label={translate('global:next')}
              onClick={() => setScreen('input')}
            />
          </StyledFooter>
        </StyledContentContainer>
      </StyledContainer>
    ),
    [parseText, setScreen, theme.gray_2, theme.white_1, translate, uri],
  )

  return handleRenderSetupScreen
}
